import React, { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, matchRoutes, useLocation } from 'react-router';
import { ChakraUIProvider, ProgressSpinner } from '@everfi/reactifi';

// TODO: custom branding
import '@everfi/reactifi/src/styles/scss/index.scss';

import { ErrorBoundary } from './lib/ErrorBoundary';

import AuthenticationContext, { AuthConfig } from './authentication/AuthenticationContext';
import Callback from './authentication/Callback';
import Impersonate from './authentication/Impersonate';
import { TokenStore } from './authentication/TokenStore';
import environment from './environment';
import { locales } from './lib/i18n';
import Main from './layout/Main';

const { HOMEROOM, ADMINIFI, NEXT } = environment;

const AUTH_CONFIGS: Record<string, AuthConfig> = {
  K12: { app: HOMEROOM, platform: 'k12', userType: 'Homeroom' },
  FOUNDRY_ADMIN: { app: ADMINIFI, platform: 'foundry', userType: 'Administrator' },
  FOUNDRY_USER: { app: ADMINIFI, platform: 'foundry', userType: 'User' },
  // NEXT_USER:  { app: NEXT, platform: 'foundry', userType: 'User' }, // disabled — next_learner route stale
};

const Admin = lazy(() => import('./admin'));
const Cportal = lazy(() => import('./cportal'));
const Student = lazy(() => import('./student'));
const Teacher = lazy(() => import('./teacher'));
const Unsubscribed = lazy(() => import('./unauthenticated/Unsubscribed'));
const TeacherRegistration = lazy(() => import('./teacher/Register'));
const Ceos = lazy(() => import('./ceos'));
const K12Student = lazy(() => import('./K12student'));
const ContentPreview = lazy(() => import('./cportal/ContentPreview'));
const Interstitial = lazy(() => import('./cportal/Interstitial'));
const ScormLocationHarness = lazy(() => import('./cportal/ScormLocationHarness'));
const ParentHub = lazy(() => import('./parent'));
// const NextLearner = lazy(() => import('./next')); // disabled — next_learner route stale

function LocaleRoute() {
  const location = useLocation();
  // TODO: implement the real list of locales
  const localeRoutes = locales.map(locale => {
    return { path: `${locale}/*` };
  });
  const matchedRoutes = matchRoutes(localeRoutes, location);

  const defaultRoutes = (
    <Route>
      <Route element={<AuthenticationContext config={AUTH_CONFIGS.FOUNDRY_ADMIN} />}>
        <Route element={<Main sidebar={true} portal="fit" />}>
          <Route path={`admin/*`} element={<Admin />} />
        </Route>
      </Route>
      <Route element={<AuthenticationContext config={AUTH_CONFIGS.FOUNDRY_USER} />}>
        <Route element={<Main sidebar={false} portal="learner" systemNotifications={true} />}>
          <Route path={`:slug/student/*`} element={<Student />} />
        </Route>
        {/* ceos routes use a shared portal with a dynamic sidebar */}
        <Route element={<Main sidebar={true} portal="ceos" />}>
          <Route path=":slug/ceos/*" element={<Ceos />} />
        </Route>
        {/* separate cportal sections - no sidebar or impersonation */}
        <Route element={<Main sidebar={false} hideImpersonation={true} portal="customer" />}>
          <Route path={`:slug/cportal/content_preview/*`} element={<ContentPreview />} />
          <Route path={`:slug/cportal/scorm_location_harness/*`} element={<ScormLocationHarness />} />
        </Route>
        {/* separate cportal sections - no sidebar */}
        <Route element={<Main sidebar={false} portal="customer" />}>
          <Route path={`:slug/cportal/interstitial_redirect/*`} element={<Interstitial />} />
        </Route>
        {/* separate cportal sections - with sidebar */}
        <Route element={<Main sidebar={true} portal="customer" systemNotifications={true} />}>
          <Route path={`:slug/*`} element={<Cportal />} />
        </Route>
      </Route>
      <Route element={<AuthenticationContext config={AUTH_CONFIGS.K12} />}>
        <Route element={<Main sidebar={true} portal="teacher" />}>
          <Route path={`teacher/*`} element={<Teacher />} />
        </Route>
        <Route element={<Main sidebar={false} portal="teacher" />}>
          <Route path={`teacher_registration/*`} element={<TeacherRegistration />} />
        </Route>
        <Route element={<Main sidebar={false} portal="student" />}>
          <Route path={`k12student/*`} element={<K12Student />} />
        </Route>
      </Route>
      {/* next_learner route disabled — stale/incomplete, mockApis logic removed
      <Route element={<AuthenticationContext context={AUTH_CONTEXTS.NEXT_USER} />}>
        <Route element={<Main sidebar={false} portal="next_learner" />}>
          <Route path={`:slug/learner`} element={<NextLearner />} />
        </Route>
      </Route>
      */}
      <Route path="unsubscribed" element={<Unsubscribed />} />
      {/* Public, unauthenticated page outside <Main>'s Suspense: ChakraUIProvider's first render has no
          Chakra context, so this Chakra-free fallback stops Navbar/SkipNav crashing with a ContextError. */}
      <Route
        path="parent/*"
        element={
          <Suspense fallback={<ProgressSpinner pageLoader={true} />}>
            <ParentHub />
          </Suspense>
        }
      />
    </Route>
  );

  // TODO: possible routing for the various portals
  // <Route element={<Main sidebar={false} />} >
  //   <Route path={`:slug/compliance-student/*`} element={<ComplianceStudent />} />
  //   <Route path={`:slug/k12-student/*`} element={<K12Student />} />
  //   <Route path={`:slug/executive/*`} element={<Executive />} />
  // </Route>
  // <Route element={<Main sidebar={true} />} >
  //   <Route path={`admin/*`} element={<Admin />} />
  //   {/* <Route path={`:slug/cportal`} element={<Cportal />} /> */}
  //   {/* <Route path={`:slug/sponsor`} element={<Sponsor />} /> */}
  //   {/* <Route path={`:slug/district-admin`} element={<DistrictAdmin />} /> */}
  // </Route>

  return (
    <Routes>
      {matchedRoutes ? <Route path="/:locale/">{defaultRoutes}</Route> : defaultRoutes}
      <Route path="*" element={<Navigate to="../admin" replace />} />
    </Routes>
  );
}

function App() {
  return (
    // Puts a ChakraProvider above every route and ErrorBoundary's fallback, but each still gets one
    // context-free first render, so the routes below must not render a Chakra component on it.
    <ChakraUIProvider>
      <Router>
        <TokenStore>
          <ErrorBoundary>
            <Routes>
              <Route path="callback/*" element={<Callback app={ADMINIFI} />} />
              <Route path="callback-homeroom/*" element={<Callback app={HOMEROOM} fallbackUserType="Homeroom" />} />
              <Route path="callback-next/*" element={<Callback app={NEXT} fallbackUserType="Next" />} />
              <Route path="homeroom/impersonate" element={<Impersonate />} />
              <Route path="*" element={<LocaleRoute />} />
            </Routes>
          </ErrorBoundary>
        </TokenStore>
      </Router>
    </ChakraUIProvider>
  );
}

export default App;
